import { useEffect, useRef, useState } from 'react';
import { Dialog, Menu } from '@headlessui/react';
import { HelpCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { captureSupportContext, supportValidation, rateLimitCopy } from '../../lib/support-context';
import './support.css';
const ENDPOINT = '/.netlify/functions/support-request';
const empty = () => ({
  category: '',
  subject: '',
  description: '',
  request_id: crypto.randomUUID()
});
const openSupport = () => window.dispatchEvent(new Event('contact-support'));
export function SupportHelp() {
  return <Menu as="div" className="relative"><Menu.Button aria-label="Help" className="support-target text-theme-secondary hover:bg-theme-hover rounded-lg"><HelpCircle className="w-5 h-5" /></Menu.Button><Menu.Items className="absolute right-0 z-50 w-48 bg-theme-surface text-theme-primary border border-theme rounded-lg shadow-lg"><Menu.Item>{({
          active
        }) => <button className={`support-target w-full px-4 ${active ? 'bg-theme-hover' : ''}`} onClick={openSupport}>Contact support</button>}</Menu.Item></Menu.Items></Menu>;
}
export function SupportContactButton() {
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    let live = true;
    supabase.auth.getSession().then(({
      data
    }) => {
      if (live) setSignedIn(!!data.session?.user);
    }).catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  return signedIn ? <button className="support-target px-4 text-theme-primary border border-theme rounded-lg" onClick={openSupport}>Contact support</button> : null;
}
async function api(payload, file) {
  const {
    data: {
      session
    }
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Please sign in to contact support');
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  if (file) form.append('screenshot', file);
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`
    },
    body: form,
    signal: AbortSignal.timeout(30000)
  });
  const body = await response.json();
  if (!response.ok) throw Object.assign(new Error(body.error || 'Send failed'), {
    body,
    status: response.status
  });
  return body;
}
export function SupportHost() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    const open = async () => {
      const {
        data
      } = await supabase.auth.getSession();
      if (data.session?.user) setUser(data.session.user);
    };
    window.addEventListener('contact-support', open);
    return () => window.removeEventListener('contact-support', open);
  }, []);
  return user ? <SupportModal user={user} onClose={() => setUser(null)} /> : null;
}
// The user is the read-only authenticated session identity.
// eslint-disable-next-line react/prop-types
export function SupportModal({user: {id,email}, onClose}) {
  const key = `support-draft:${id}`;
  const [draft, setDraft] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      return saved && typeof saved.subject === 'string' && typeof saved.description === 'string' ? saved : empty();
    } catch {
      return empty();
    }
  });
  const [file, setFile] = useState(null),
    [image, setImage] = useState(''),
    [fileError, setFileError] = useState('');
  const [banner, setBanner] = useState(''),
    [network, setNetwork] = useState(false),
    [sending, setSending] = useState(false),
    [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null),
    [copied, setCopied] = useState(false),
    [details, setDetails] = useState(false),
    [technical, setTechnical] = useState(null),
    [touched, setTouched] = useState({});
  const busy = useRef(false),
    keep = useRef(null),
    heading = useRef(null),
    picker = useRef(null),
    category = useRef(null),
    subject = useRef(null),
    description = useRef(null);
  const refs = {
    category,
    subject,
    description
  };
  const [context] = useState(captureSupportContext);
  const errors = supportValidation(draft),
    invalid = Object.keys(errors).length > 0;
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(draft));
    } catch {/* storage unavailable: retain memory draft */}
  }, [draft, key]);
  useEffect(() => {
    if (result) heading.current?.focus();
  }, [result]);
  useEffect(() => {
    if (confirm) keep.current?.focus();
  }, [confirm]);
  useEffect(() => {
    if (!file) {
      setImage('');
      return;
    }
    const url = URL.createObjectURL(file);
    setImage(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    let live = true;
    api({
      preview: true,
      context
    }).then(b => {
      if (live) setTechnical(b.context);
    }).catch(() => {
      if (live) setTechnical(null);
    });
    return () => {
      live = false;
    };
  }, [context]);
  // Keep the sheet inside the visual viewport while the phone keyboard is open.
  useEffect(() => {
    const view = window.visualViewport;
    const resize = () => {
      document.documentElement.style.setProperty('--support-height', `${view?.height || window.innerHeight}px`);
      document.documentElement.style.setProperty('--support-top', `${view?.offsetTop || 0}px`);
    };
    resize();
    view?.addEventListener('resize', resize);
    view?.addEventListener('scroll', resize);
    return () => {
      view?.removeEventListener('resize', resize);
      view?.removeEventListener('scroll', resize);
    };
  }, []);
  const close = () => {
    if (busy.current) return;
    if (confirm) {
      setConfirm(false);
      return;
    }
    if (result) {
      onClose();
      return;
    }
    if (draft.subject || draft.description) {
      setConfirm(true);
    } else onClose();
  };
  const update = (field, value) => {
    setDraft(d => ({
      ...d,
      [field]: value,
      request_id: crypto.randomUUID()
    }));
    setTouched(t => ({
      ...t,
      [field]: true
    }));
  };
  const attach = f => {
    if (!f) return;
    setFileError('');
    if (f.size > 5 * 1024 * 1024) {
      setFileError(`Max 5 MB (this file is ${(f.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
      setFileError('Choose a PNG, JPEG or WebP image');
      return;
    }
    setDraft(d => ({ ...d, request_id: crypto.randomUUID() }));
    setFile(f);
  };
  const submit = async e => {
    e?.preventDefault();
    if (busy.current) return;
    setTouched({
      category: true,
      subject: true,
      description: true
    });
    if (invalid) {
      refs[Object.keys(errors)[0]].current?.focus();
      return;
    }
    busy.current = true;
    setSending(true);
    setBanner('');
    try {
      const payload = {
        ...draft,
        context,
        website: document.getElementById('support-website')?.value || ''
      };
      let response;
      if (file) {
        // Upload directly to a private, signed object URL so a 5 MB attachment
        // does not exceed the function platform's buffered/base64 request limit.
        const prepared = await api({
          ...payload,
          prepare_upload: true,
          screenshot_size: file.size,
          screenshot_type: file.type
        });
        if (prepared.upload) {
          const uploaded = await supabase.storage.from('support-screenshots').uploadToSignedUrl(prepared.upload.path, prepared.upload.token, file, {
            contentType: file.type
          });
          if (uploaded.error) throw Object.assign(new Error('Upload failed'), {
            body: {
              field: 'screenshot',
              error: 'Upload failed. Try again, or send without a screenshot'
            }
          });
          response = await api({
            ...payload,
            request_id: prepared.request_id,
            uploaded_screenshot: true
          });
        } else response = prepared;
      } else response = await api(payload);
      if (response.context) setTechnical(response.context);
      setResult(response);
      try {
        sessionStorage.removeItem(key);
      } catch {/* memory only */}
    } catch (err) {
      if (err.body?.field === 'screenshot') {
        setDraft(d => ({ ...d, request_id: crypto.randomUUID() }));
        setFileError(err.body.error);
        setBanner('');
        setNetwork(false);
      } else {
        setBanner(err.status === 429 ? rateLimitCopy(err.body) : err.status === 400 ? err.body?.error : "Couldn't send. Your message is saved.");
        setNetwork(err.status !== 429 && err.status !== 400);
      }
    } finally {
      busy.current = false;
      setSending(false);
    }
  };
  const fieldError = name => touched[name] && errors[name] ? <p id={`support-${name}-error`} className="text-red-600 dark:text-red-400 text-sm" role="alert">⚠ {errors[name]}</p> : null;
  const attrs = name => ({
    id: `support-${name}`,
    ref: refs[name],
    'aria-invalid': !!(touched[name] && errors[name]),
    'aria-describedby': touched[name] && errors[name] ? `support-${name}-error` : undefined
  });
  return <Dialog open onClose={close} className="support-dialog" initialFocus={category}>
    <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
    <div className="support-position"><Dialog.Panel className="support-sheet bg-theme-surface text-theme-primary border border-theme rounded-xl shadow-xl" aria-busy={sending}>
      <header className="flex items-center justify-between p-4 border-b border-theme"><Dialog.Title className="text-lg font-semibold">Contact support</Dialog.Title><button className="support-target rounded-lg" aria-label="Close" onClick={close} disabled={sending}>✕</button></header>
      {result ? <div className="support-content p-5">
        <h2 ref={heading} tabIndex={-1} role="status" className="text-xl font-semibold">{result.status === 'filed' ? 'Request sent' : "Received — we'll file it shortly"}</h2>
        {result.status === 'queued' ? <p role="alert" className="mt-4">Our ticket service isn&apos;t responding. Your message is saved. We&apos;ll still reply to {email}.</p> : <p className="mt-4">We&apos;ll reply to {email} by email.</p>}
        <div className="flex items-center gap-3 mt-5"><span className="select-text font-mono">{result.reference}</span><button className="support-target border border-theme rounded-lg px-3" aria-label={`Copy reference ${result.reference}`} onClick={async () => {
              try {
                await navigator.clipboard.writeText(result.reference);
                setCopied(true);
              } catch {
                setCopied(false);
              }
            }}>{copied ? 'Copied' : 'Copy'}</button></div><p aria-live="polite" className="text-sm text-theme-secondary">{copied ? 'Reference copied' : ''}</p>
        {result.context && <div className="mt-4"><button type="button" className="support-target text-sm text-theme-secondary" aria-expanded={details} onClick={()=>setDetails(!details)}>Technical details attached</button>{details && <pre className="text-xs text-theme-secondary whitespace-pre-wrap break-all">{JSON.stringify(result.context,null,2)}</pre>}</div>}
        <button onClick={onClose} className="support-target mt-5 px-4 border border-theme rounded-lg">Close</button>
      </div> : <form onSubmit={submit} className="support-form" noValidate onPaste={e => {
          if (sending) return;
          const f = Array.from(e.clipboardData.files || [])[0];
          if (f) {
            e.preventDefault();
            attach(f);
          }
        }}>
        <div className="support-content p-5 space-y-4">
          <p className="text-sm text-theme-secondary">Sending as {email} · account {id}</p>
          {banner && <div role="alert" className="border border-theme bg-theme-hover rounded-lg p-3">{banner}</div>}
          <div><label htmlFor="support-category">Category</label><select {...attrs('category')} value={draft.category} required disabled={sending} onBlur={() => setTouched(t => ({
                ...t,
                category: true
              }))} onChange={e => update('category', e.target.value)} className="support-input bg-theme-primary border-theme"><option value="">Choose a category</option><option value="broken">Something&apos;s broken</option><option value="question">Question</option><option value="billing">Billing</option><option value="other">Other</option></select>{fieldError('category')}</div>
          <div><label htmlFor="support-subject">Subject</label><input {...attrs('subject')} className="support-input bg-theme-primary border-theme" value={draft.subject} required readOnly={sending} onBlur={() => setTouched(t => ({
                ...t,
                subject: true
              }))} onChange={e => update('subject', e.target.value)} /><p className={`text-xs text-theme-secondary ${draft.subject.length > 120 ? 'support-over' : ''}`}>{draft.subject.length} / 120</p>{fieldError('subject')}</div>
          <div><label htmlFor="support-description">Description</label><textarea {...attrs('description')} rows={5} className="support-input bg-theme-primary border-theme" value={draft.description} required readOnly={sending} onBlur={() => setTouched(t => ({
                ...t,
                description: true
              }))} onChange={e => update('description', e.target.value)} /><p className={`text-xs text-theme-secondary ${draft.description.length > 5000 ? 'support-over' : ''}`}>{draft.description.length} / 5000</p><p className="text-sm text-theme-secondary">Don&apos;t include passwords or card numbers.</p>{fieldError('description')}</div>
          <div className="border border-dashed border-theme rounded-lg p-3" onDragOver={e => e.preventDefault()} onDrop={e => {
              e.preventDefault();
              if (!sending) attach(e.dataTransfer.files[0]);
            }}>
            <p>Screenshot (optional)</p><input ref={picker} type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={sending} onChange={e => {
                attach(e.target.files[0]);
                e.target.value = '';
              }} />
            <button type="button" className="support-target px-3 border border-theme rounded-lg my-2" disabled={sending} onClick={() => picker.current.click()}>Browse files</button><span className="text-sm text-theme-secondary ml-2">or paste an image</span>
            {file && <div><img src={image} alt="Screenshot preview" className="max-h-40 max-w-full" /><button type="button" className="support-target px-3" disabled={sending} onClick={() => {
                  setDraft(d => ({ ...d, request_id: crypto.randomUUID() }));
                  setFile(null);
                  setFileError('');
                }}>Remove screenshot</button></div>}
            {fileError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">⚠ {fileError}</p>}
            <p className="text-sm text-theme-secondary">Screenshots can include personal info. Crop or remove anything you don&apos;t want to share.</p>
          </div>
          <div><button type="button" className="support-target w-full justify-between text-sm text-theme-secondary" aria-expanded={details} onClick={() => setDetails(!details)}>Include technical details (always attached) {details ? '−' : '+'}</button>{details && <div className="text-xs text-theme-secondary"><p>Server time is authoritative; the submission timestamp updates when you send.</p><pre className="whitespace-pre-wrap break-all">{technical ? JSON.stringify(technical, null, 2) : 'Loading account and recent server errors…'}</pre></div>}</div>
          <div className="support-honeypot" aria-hidden="true"><label htmlFor="support-website">Website</label><input id="support-website" tabIndex={-1} autoComplete="off" readOnly={sending} /></div>
        </div>
        <footer className="support-footer border-t border-theme bg-theme-surface p-4 flex justify-end gap-3"><button type="button" disabled={sending} className="support-target px-3" onClick={network ? onClose : close}>{network ? 'Close (saved for this tab)' : 'Cancel'}</button><button type="submit" disabled={invalid || sending} className="support-send support-target px-5 rounded-lg text-accent-foreground">{sending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{sending ? network ? 'Retrying…' : 'Sending…' : network ? 'Retry' : 'Send'}</button></footer>
      </form>}
      {confirm && <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-4" onKeyDown={e => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setConfirm(false);
          }
        }}><div onKeyDown={e => {
            if (e.key === 'Tab') {
              const buttons = e.currentTarget.querySelectorAll('button');
              if (e.shiftKey && document.activeElement === buttons[0]) {
                e.preventDefault();
                buttons[1].focus();
              } else if (!e.shiftKey && document.activeElement === buttons[1]) {
                e.preventDefault();
                buttons[0].focus();
              }
            }
          }} role="alertdialog" aria-modal="true" aria-labelledby="support-discard-title" aria-describedby="support-discard-copy" className="bg-theme-surface text-theme-primary p-5 rounded-xl border border-theme"><h2 id="support-discard-title">Discard your message?</h2><p id="support-discard-copy" className="text-sm text-theme-secondary my-3">Your draft is kept for this tab unless you discard it.</p><div className="flex gap-3"><button type="button" ref={keep} className="support-target px-3 border border-theme rounded-lg" onClick={() => setConfirm(false)}>Keep editing</button><button type="button" className="support-target px-3" onClick={() => {
                sessionStorage.removeItem(key);
                onClose();
              }}>Discard</button></div></div></div>}
    </Dialog.Panel></div>
  </Dialog>;
}
